import React, { useEffect, useState } from 'react';
import { Card } from './ui/card';
import { Users, TrendingUp, DollarSign, Gamepad2, Award, Activity } from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { api } from '../utils/api';
import { toast } from 'sonner';

const COLORS = ['#4169E1', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

export default function AnalyticsDashboard() {
  const [overview, setOverview] = useState(null);
  const [dailySignups, setDailySignups] = useState([]);
  const [levelStats, setLevelStats] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      const [overviewRes, signupsRes, levelsRes, activityRes] = await Promise.all([
        api.get('/admin/analytics/overview'),
        api.get('/admin/analytics/daily-signups'),
        api.get('/admin/analytics/level-stats'),
        api.get('/admin/analytics/recent-activity')
      ]);

      setOverview(overviewRes.data);
      setDailySignups(signupsRes.data.daily_signups);
      setLevelStats(levelsRes.data.level_stats);
      setRecentActivity(activityRes.data.recent_activity);
      setLoading(false);
    } catch (error) {
      console.error('Analytics error:', error);
      toast.error('Failed to load analytics');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4169E1] mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading analytics...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-600 font-medium">Total Users</p>
              <p className="text-3xl font-bold text-blue-900">{overview?.total_users || 0}</p>
              <p className="text-xs text-blue-600 mt-1">+{overview?.new_users_7d || 0} this week</p>
            </div>
            <Users className="w-12 h-12 text-blue-500" />
          </div>
        </Card>

        <Card className="p-6 bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600 font-medium">Premium Users</p>
              <p className="text-3xl font-bold text-green-900">{overview?.premium_users || 0}</p>
              <p className="text-xs text-green-600 mt-1">
                {overview?.total_users > 0 ? 
                  `${Math.round((overview.premium_users / overview.total_users) * 100)}% conversion` : 
                  '0% conversion'}
              </p>
            </div>
            <Award className="w-12 h-12 text-green-500" />
          </div>
        </Card>

        <Card className="p-6 bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-purple-600 font-medium">Total Revenue</p>
              <p className="text-3xl font-bold text-purple-900">${overview?.total_revenue || 0}</p>
              <p className="text-xs text-purple-600 mt-1">$3 per premium user</p>
            </div>
            <DollarSign className="w-12 h-12 text-purple-500" />
          </div>
        </Card>

        <Card className="p-6 bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-orange-600 font-medium">Games Played</p>
              <p className="text-3xl font-bold text-orange-900">{overview?.total_games_played || 0}</p>
              <p className="text-xs text-orange-600 mt-1">Total sessions</p>
            </div>
            <Gamepad2 className="w-12 h-12 text-orange-500" />
          </div>
        </Card>

        <Card className="p-6 bg-gradient-to-br from-pink-50 to-pink-100 border-pink-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-pink-600 font-medium">Active Users</p>
              <p className="text-3xl font-bold text-pink-900">{overview?.active_users_7d || 0}</p>
              <p className="text-xs text-pink-600 mt-1">Last 7 days</p>
            </div>
            <Activity className="w-12 h-12 text-pink-500" />
          </div>
        </Card>

        <Card className="p-6 bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-indigo-600 font-medium">Engagement</p>
              <p className="text-3xl font-bold text-indigo-900">
                {overview?.total_users > 0 ? 
                  Math.round((overview.active_users_7d / overview.total_users) * 100) : 0}%
              </p>
              <p className="text-xs text-indigo-600 mt-1">Weekly retention</p>
            </div>
            <TrendingUp className="w-12 h-12 text-indigo-500" />
          </div>
        </Card>
      </div>

      {/* Daily Signups Chart */}
      <Card className="p-6">
        <h3 className="text-lg font-bold text-gray-800 mb-4">Daily Signups (Last 14 Days)</h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={dailySignups}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="_id" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="count" stroke="#4169E1" strokeWidth={2} name="Signups" />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      {/* Level Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Level Play Counts</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={levelStats}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="level" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="total_plays" fill="#4169E1" name="Total Plays" />
              <Bar dataKey="completed" fill="#10B981" name="Completed" />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Completion Rates by Level</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={levelStats}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={(entry) => `L${entry.level}: ${entry.completion_rate}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="completion_rate"
              >
                {levelStats.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card className="p-6">
        <h3 className="text-lg font-bold text-gray-800 mb-4">Recent Activity</h3>
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {recentActivity.map((event, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className={`w-2 h-2 rounded-full ${
                  event.event_type === 'signup' ? 'bg-blue-500' :
                  event.event_type === 'login' ? 'bg-green-500' :
                  event.event_type === 'level_complete' ? 'bg-purple-500' :
                  event.event_type === 'premium_purchase' ? 'bg-yellow-500' :
                  'bg-gray-500'
                }`}></div>
                <div>
                  <p className="text-sm font-medium text-gray-800">
                    {event.user_name || 'User'} 
                    {event.event_type === 'signup' && ' signed up'}
                    {event.event_type === 'login' && ' logged in'}
                    {event.event_type === 'level_complete' && ` completed Level ${event.level}`}
                    {event.event_type === 'premium_purchase' && ' purchased Premium'}
                  </p>
                  <p className="text-xs text-gray-500">
                    {new Date(event.timestamp).toLocaleString()}
                  </p>
                </div>
              </div>
              {event.score && (
                <span className="text-sm font-semibold text-gray-600">
                  Score: {event.score}
                </span>
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
